import React from 'react';

import MermaidBlock from '../../../conversation/MermaidBlock';

/** Inline-render any tool result whose envelope carries a `mermaid_code`
 * field — covers `generate_mermaid_diagram`, `mindmap`, and any future
 * Mermaid producer.
 *
 * Why this exists (2026-05-03 P0 follow-up): mindmap_tool emits a tree
 * dict that no chat-panel renderer parsed, so a successful mindmap
 * call landed as prose-only "the mindmap…has been generated" with no
 * visual output. This renderer pulls `result.mermaid_code` (now also
 * emitted by mindmap_tool, converted from the tree) and delegates to
 * MermaidBlock — same path the chat panel uses for fenced ```mermaid
 * code blocks in assistant text replies. Saves the model from
 * couriering Mermaid syntax through prose.
 *
 * The component intentionally has no save flow — Mermaid producers
 * persist via the canonical `create_artifacts(artifact_type='mermaid'|
 * 'mindmap')` path with blackboard auto-resolve. No "Save as artifact"
 * button needed inline; the persona prompt directs the model to call
 * create_artifacts when the user asks to save. */
interface RendererProps {
  toolCall: any;
  darkMode: boolean;
}

const _innerOf = (toolCall: any): any => {
  const result = toolCall?.result;
  return (result && typeof result === 'object' && 'result' in result)
    ? (result as any).result
    : result;
};

export const MermaidInlineRenderer: React.FC<RendererProps> = ({ toolCall, darkMode }) => {
  const inner = _innerOf(toolCall);
  const code: string | null = inner?.mermaid_code || null;
  if (!code) return null;

  // Caption: pick the most informative envelope field. mindmap supplies
  // node_count + title; mermaid supplies diagram_type + title.
  const caption =
    inner.title
    || (inner.node_count != null ? `${inner.node_count} nodes` : null)
    || inner.diagram_type
    || null;
  const toolName = toolCall?.tool_name || toolCall?.name;
  const familyLabel =
    toolName === 'mindmap' ? 'mindmap' :
    toolName === 'generate_mermaid_diagram' ? 'diagram' :
    'mermaid';

  return (
    <div
      className={`rounded-lg border ${darkMode ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-white'} p-3`}
      data-testid="mermaid-inline-renderer"
    >
      <MermaidBlock code={code} darkMode={darkMode}>
        <pre><code>{code}</code></pre>
      </MermaidBlock>
      <div className={`mt-2 text-[11px] ${darkMode ? 'text-gray-400' : 'text-gray-500'} flex items-center gap-2`}>
        <span className="opacity-70">●</span>
        <span>
          {caption ? `${caption} · ` : ''}{familyLabel} · ephemeral preview
        </span>
      </div>
    </div>
  );
};
